import { DEFAULT_PAGINATION } from "@/constants";
import useControlledState from "@/hooks/useControlledState";
import { Checkbox, Pagination as PaginationElement } from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { ReactNode } from "react";
import Skeleton from "../Skeleton/Skeleton";
import EmptyIcon from "../icons/EmptyIcon";
import ColumnHeader, { OnSortFilterSearchChange } from "./ColumnHeader";
import Row from "./Row";
import styles from "./Table.module.scss";

export interface DataRecord {
  [key: string]: any;
}

export type ColumnType = "checkbox" | "normal";

export interface Column<R = DataRecord> {
  key: string;
  label: string | ReactNode;
  sortable?: boolean;
  searchable?: boolean;
  filterable?: boolean;
  minWidth?: string;
  maxWidth?: string;
  type?: ColumnType;
  noLink?: boolean;
  align?: "start" | "end";
  renderer?: (record: R, key: string) => ReactNode;
  cellProps?: (
    record: R,
    key: string
  ) => {
    style: React.CSSProperties;
  };
  cellTooltip?: (record: R, key: string) => ReactNode;
}

export type SortOrder = "ascend" | "descend";

export interface Pagination {
  page: number;
  pageSize: number;
}

export type Filter = Record<string, any[]>;

export interface AvailableFilters {
  [key: string]: {
    label: string;
    value: any;
  }[];
}

export type Search = Record<string, string>;

export type Sort = {
  key: string;
  order: SortOrder;
} | null;

export type OnSearchFilterSortPaginate = ({
  pagination,
  filter,
  search,
  sort,
}: {
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort | null;
}) => void;

export interface RowRendererProps<R = DataRecord> {
  record: R;
  columns: Column<R>[];
  gridTemplateColumns: string;
  rowKey: string;
  selectedKeys: any[];
  onChangeCheckbox: (e: CheckboxChangeEvent) => void;
  rowLink?: (record: R) => string | undefined;
  onRowClick?: (record: R) => void;
  activeRowKey?: string;
  className?: string;
}

export interface TableProps<R = DataRecord> {
  columns: Column<R>[];
  data?: R[];
  loading?: boolean;
  checkable?: boolean;
  totalRows?: number;
  sort?: Sort;
  search?: Search;
  filter?: Filter;
  rowKey: string;
  availableFilters?: AvailableFilters;
  pagination?: Pagination;
  selectedKeys?: any[];
  activeRowKey?: any;
  rowLink?: (record: R) => string | undefined;
  onRowClick?: (record: R) => void;
  hideHeader?: boolean;
  onSearchFilterSortPaginate?: OnSearchFilterSortPaginate;
  onSelectedKeysChange?: (keys: any[]) => void;
  rowRenderer?: (props: RowRendererProps<R>) => JSX.Element;
  hideRaster?: boolean;
  type?: "grey";
  emptyState?: {
    noResultsMessage?: ReactNode;
    noResultsForSelectionMessage?: ReactNode;
    hide?: boolean;
  };
}

export default function Table<R extends DataRecord>({
  data,
  loading,
  columns,
  checkable,
  rowLink,
  rowKey,
  totalRows,
  onSearchFilterSortPaginate,
  onSelectedKeysChange,
  availableFilters,
  selectedKeys: parentSelectedKeys,
  pagination: parentPagination,
  sort: parentSort,
  search: parentSearch,
  filter: parentFilter,
  rowRenderer: RowRenderer,
  hideHeader,
  activeRowKey,
  hideRaster,
  onRowClick,
  type,
  emptyState,
}: TableProps<R>) {
  let [selectedKeys, setSelectedKeys] = useControlledState<any[]>(
    [],
    parentSelectedKeys,
    onSelectedKeysChange
  );
  const [pagination, setPagination] = useControlledState<Pagination>(
    DEFAULT_PAGINATION,
    parentPagination,
    (pagination) => onSearchFilterSortPaginate?.({ pagination })
  );
  const [filter, setFilter] = useControlledState<Filter>(
    {},
    parentFilter,
    (filter) => onSearchFilterSortPaginate?.({ filter, pagination: { ...pagination, page: 1 } })
  );
  const [search, setSearch] = useControlledState<Search>(
    {},
    parentSearch,
    (search) => onSearchFilterSortPaginate?.({ search, pagination: { ...pagination, page: 1 } })
  );
  const [sort, setSort] = useControlledState<Sort | null>(
    null,
    parentSort,
    (sort) => onSearchFilterSortPaginate?.({ sort, pagination: { ...pagination, page: 1 } })
  );

  if (checkable) {
    columns = [
      {
        label: loading ? (
          <Skeleton style={{ width: "15px", height: "15px", borderRadius: "3px" }} />
        ) : (
          (data || []).length > 0 && (
            <Checkbox
              onChange={(e) =>
                setSelectedKeys((selectedKeys) => {
                  let currentlyCheckedWithoutProductsInView = selectedKeys.filter(
                    (x) => !data?.map((x) => x[rowKey]).includes(x)
                  );
                  if (e.target.checked) {
                    return [
                      ...currentlyCheckedWithoutProductsInView,
                      ...(data?.map((x) => x[rowKey]) || []),
                    ];
                  }
                  return currentlyCheckedWithoutProductsInView;
                })
              }
              checked={data?.every((x) => selectedKeys?.includes(x[rowKey]))}
            />
          )
        ),
        key: "checkbox",
        type: "checkbox",
        noLink: true,
        minWidth: "40px",
        maxWidth: "40px",
      },
      ...columns,
    ];
  }

  const gridTemplateColumns = columns
    .map(
      (column) =>
        `minmax(${column.minWidth ? column.minWidth : "120px"}, ${
          column.maxWidth ? column.maxWidth : "1fr"
        })`
    )
    .join(" ");

  const onChangeCheckbox = (e: CheckboxChangeEvent) => {
    setSelectedKeys(() => {
      let newSelection: number[] = selectedKeys?.filter((x) => x !== e.target.value) || [];
      if (e.target.checked) {
        newSelection.push(e.target.value);
      }
      return newSelection;
    });
  };

  const onSortFilterSearchChange: OnSortFilterSearchChange = ({
    key,
    searchTerm,
    filters,
    sortOrder,
  }) => {
    if (searchTerm !== undefined) {
      setSearch((search) => {
        let { [key]: _, ...newSearch } = search;
        if (searchTerm) {
          newSearch[key] = searchTerm;
        }
        return newSearch;
      });
    }

    if (filters !== undefined) {
      setFilter((filter) => {
        let { [key]: _, ...newFilter } = filter;
        if (filters && filters.length) {
          newFilter[key] = filters;
        }
        return newFilter;
      });
    }

    if (sortOrder !== undefined) {
      setSort(sortOrder === null ? sortOrder : { key, order: sortOrder });
    }
  };

  return (
    <>
      <div
        className={[styles.Table, hideRaster && styles["hide-raster"], type && styles[type]].join(
          " "
        )}
      >
        {hideHeader ? (
          ""
        ) : (
          <div
            className={`${styles.row} ${styles.header}`}
            style={{ gridTemplateColumns }}
          >
            {columns.map((column) => {
              return (
                <ColumnHeader
                  key={column.key}
                  column={column}
                  availableFilters={availableFilters || {}}
                  activeSort={sort}
                  activeSearch={search}
                  activeFilter={filter}
                  onSortFilterSearchChange={onSortFilterSearchChange}
                />
              );
            })}
          </div>
        )}
        {loading === true ? (
          [...Array(Math.min(pagination.pageSize)).keys()].map((i) => {
            return (
              <div
                key={i}
                className={`${styles.row} ${styles.skeleton}`}
              >
                <div>&nbsp;</div>
              </div>
            );
          })
        ) : (data || []).length === 0 && emptyState?.hide !== true ? (
          <div className={styles.empty}>
            <EmptyIcon />
            {Object.keys({ ...search, ...filter }).length > 0
              ? emptyState?.noResultsForSelectionMessage
                ? emptyState.noResultsForSelectionMessage
                : "No results for this selection."
              : emptyState?.noResultsMessage
                ? emptyState.noResultsMessage
                : "No results."}
          </div>
        ) : (
          data?.map((record) => {
            if (RowRenderer)
              return (
                <RowRenderer
                  key={record[rowKey]}
                  {...{
                    rowLink,
                    onRowClick,
                    activeRowKey,
                    record,
                    columns,
                    gridTemplateColumns,
                    rowKey,
                    selectedKeys: selectedKeys,
                    onChangeCheckbox,
                  }}
                />
              );

            return (
              <Row
                key={record[rowKey]}
                {...{
                  rowLink,
                  onRowClick,
                  record,
                  activeRowKey,
                  columns,
                  gridTemplateColumns,
                  rowKey,
                  selectedKeys: selectedKeys,
                  onChangeCheckbox,
                }}
              />
            );
          })
        )}
      </div>
      <div className={styles.pagination}>
        <PaginationElement
          pageSizeOptions={[10, 20, 50, 100, 500]}
          current={pagination.page}
          pageSize={pagination.pageSize}
          hideOnSinglePage={true}
          total={totalRows}
          onChange={(page, pageSize) => setPagination({ page, pageSize })}
        />
      </div>
    </>
  );
}
